import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  MessageCircle, X, Send, Minimize2, User, Bot,
  Phone, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Générer un ID visiteur unique
const getVisitorId = () => {
  let visitorId = localStorage.getItem("jyvis_visitor_id");
  if (!visitorId) {
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("jyvis_visitor_id", visitorId);
  }
  return visitorId;
};

// Générer un ID session
const getSessionId = () => {
  let sessionId = sessionStorage.getItem("jyvis_chat_session");
  if (!sessionId) {
    sessionId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem("jyvis_chat_session", sessionId);
  }
  return sessionId;
};

export default function AIChatBot({ raised = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showProactiveMessage, setShowProactiveMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Message d'accueil initial
  const welcomeMessage = {
    role: "assistant",
    content: "Bonjour ! 👋 Je suis Sophie, votre conseillère JYVIS. Comment puis-je vous aider aujourd'hui ?",
    timestamp: new Date().toISOString()
  };

  // Initialisation
  useEffect(() => {
    setSessionId(getSessionId());
    
    // Afficher le message proactif après 15 secondes
    const proactiveTimer = setTimeout(() => {
      if (!isOpen && messages.length === 0) {
        setShowProactiveMessage(true);
      }
    }, 15000);

    return () => clearTimeout(proactiveTimer);
  }, []);

  // Scroll vers le bas quand nouveaux messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus sur l'input quand ouvert
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  const openChat = () => {
    setIsOpen(true);
    setIsMinimized(false);
    setShowProactiveMessage(false);
    
    // Ajouter message de bienvenue si première fois
    if (messages.length === 0) {
      setMessages([welcomeMessage]);
    }

    // Track event
    trackEvent("chat_opened");
  };

  const closeChat = () => {
    setIsOpen(false);
    setIsMinimized(false);
  };

  const minimizeChat = () => {
    setIsMinimized(true);
  };

  const trackEvent = async (eventType, data = {}) => {
    try {
      await axios.post(`${API}/api/ai/track`, {
        visitor_id: getVisitorId(),
        session_id: getSessionId(),
        event_type: eventType,
        data
      });
    } catch (e) {
      // Silent fail for tracking
    }
  };

  const appendToLastAssistant = (text, extra = {}) => {
    setMessages(prev => {
      const copy = [...prev];
      const last = copy[copy.length - 1];
      copy[copy.length - 1] = { ...last, content: last.content + text, ...extra };
      return copy;
    });
  };

  const sendMessage = async (overrideText) => {
    const text = (overrideText ?? inputValue).trim();
    if (!text || isLoading) return;

    setMessages(prev => [...prev, { role: "user", content: text, timestamp: new Date().toISOString() }]);
    setInputValue("");
    setIsLoading(true);

    let started = false;
    try {
      const res = await fetch(`${API}/api/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text, visitor_id: getVisitorId() })
      });
      if (!res.ok) throw new Error(res.status === 429 ? "rate" : "http");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop();
        for (const evt of events) {
          if (!evt.startsWith("data: ")) continue;
          const data = JSON.parse(evt.slice(6));
          if (data.type === "session") setSessionId(data.session_id);
          if (data.type === "delta" || data.type === "error") {
            if (!started) {
              started = true;
              setIsLoading(false);
              setMessages(prev => [...prev, { role: "assistant", content: "", timestamp: new Date().toISOString() }]);
            }
            appendToLastAssistant(data.content, data.type === "error" ? { isError: true } : {});
          }
        }
      }
      trackEvent("chat_message_sent", { message_length: text.length });
    } catch (error) {
      console.error("Chat error:", error);
      if (!started) {
        setMessages(prev => [...prev, {
          role: "assistant",
          content: error.message === "rate"
            ? "Vous m'avez envoyé beaucoup de messages : réessayez dans quelques minutes, ou appelez-nous au 01 48 20 98 27."
            : "Désolée, je rencontre un souci technique. Appelez-nous au 01 48 20 98 27 ! 📞",
          timestamp: new Date().toISOString(),
          isError: true
        }]);
      }
    }
    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Quick replies
  const quickReplies = [
    "Devis auto 🚗",
    "Assurance temporaire ⏱️",
    "Parler à un conseiller 📞"
  ];

  const handleQuickReply = (reply) => {
    sendMessage(reply);
  };

  return (
    <>
      {/* Message proactif */}
      <AnimatePresence>
        {showProactiveMessage && !isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.9 }}
            className="fixed bottom-24 left-6 z-50 bg-white rounded-2xl shadow-2xl p-4 max-w-[280px] border border-gray-100"
          >
            <button
              onClick={() => setShowProactiveMessage(false)}
              className="absolute -top-2 -right-2 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200"
            >
              <X className="w-3 h-3" />
            </button>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#102692] to-[#2563EB] rounded-full flex items-center justify-center flex-shrink-0">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">Besoin d'aide ?</p>
                <p className="text-xs text-gray-500 mt-1">Sophie peut vous aider à trouver la meilleure assurance</p>
                <Button 
                  size="sm" 
                  onClick={openChat}
                  className="mt-2 bg-[#102692] hover:bg-[#0d1f75] text-white text-xs"
                >
                  Discuter maintenant
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bouton d'ouverture */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={openChat}
            className={`fixed ${raised ? "bottom-20" : "bottom-4"} left-4 sm:bottom-6 sm:left-6 z-50 w-14 h-14 bg-gradient-to-br from-[#102692] to-[#2563EB] rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-shadow`}
            data-testid="ai-chatbot-btn"
          >
            <MessageCircle className="w-6 h-6 text-white" />
            {messages.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#F97316] rounded-full text-white text-xs flex items-center justify-center">
                {messages.filter(m => m.role === "assistant").length}
              </span>
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Fenêtre de chat */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              height: isMinimized ? "auto" : "500px"
            }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            className="fixed bottom-4 left-4 right-4 sm:right-auto sm:bottom-6 sm:left-6 z-50 sm:w-[380px] max-h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            data-testid="ai-chatbot-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#102692] to-[#2563EB] px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">Sophie - Conseillère IA</h3>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    <span className="text-white/70 text-xs">En ligne</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button 
                  onClick={minimizeChat}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <Minimize2 className="w-4 h-4 text-white" />
                </button>
                <button 
                  onClick={closeChat}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            {/* Messages */}
            {!isMinimized && (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                  {messages.map((message, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex items-end gap-2 ${
                        message.role === "user" ? "flex-row-reverse" : ""
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        message.role === "user" 
                          ? "bg-gray-200" 
                          : "bg-gradient-to-br from-[#102692] to-[#2563EB]"
                      }`}>
                        {message.role === "user" 
                          ? <User className="w-4 h-4 text-gray-600" />
                          : <Bot className="w-4 h-4 text-white" />
                        }
                      </div>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                        message.role === "user"
                          ? "bg-[#102692] text-white rounded-br-sm"
                          : message.isError
                          ? "bg-red-100 text-red-800 rounded-bl-sm"
                          : "bg-white text-gray-800 shadow-sm rounded-bl-sm"
                      }`}>
                        <p className="text-sm whitespace-pre-wrap" data-testid={`chat-message-${message.role}-${index}`}>{message.content}</p>
                      </div>
                    </motion.div>
                  ))}
                  
                  {/* Typing indicator */}
                  {isLoading && (
                    <div className="flex items-end gap-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-[#102692] to-[#2563EB] rounded-full flex items-center justify-center">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                      <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Quick replies */}
                {messages.length <= 2 && (
                  <div className="px-4 py-2 bg-gray-50 border-t flex gap-2 overflow-x-auto">
                    {quickReplies.map((reply, index) => (
                      <button
                        key={index}
                        onClick={() => handleQuickReply(reply)}
                        data-testid={`chat-quick-reply-${index}`}
                        className="flex-shrink-0 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs text-gray-700 hover:bg-gray-100 hover:border-[#102692] transition-colors"
                      >
                        {reply}
                      </button>
                    ))}
                  </div>
                )}

                {/* Input */}
                <div className="p-3 bg-white border-t">
                  <div className="flex items-center gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Écrivez votre message..."
                      className="flex-1 px-4 py-2 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#102692]/20"
                      disabled={isLoading}
                      data-testid="chat-input"
                    />
                    <Button
                      onClick={() => sendMessage()}
                      disabled={!inputValue.trim() || isLoading}
                      size="icon"
                      className="w-10 h-10 rounded-full bg-[#102692] hover:bg-[#0d1f75]"
                      data-testid="chat-send-btn"
                    >
                      {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  
                  {/* Footer */}
                  <div className="flex items-center justify-between mt-2 px-1">
                    <p className="text-[10px] text-gray-400">Propulsé par IA JYVIS</p>
                    <a 
                      href="tel:0148209827" 
                      className="text-[10px] text-[#102692] hover:underline flex items-center gap-1"
                    >
                      <Phone className="w-3 h-3" />
                      01 48 20 98 27
                    </a>
                  </div>
                </div>
              </>
            )}

            {/* Minimized state */}
            {isMinimized && (
              <button 
                onClick={() => setIsMinimized(false)}
                className="p-4 text-left hover:bg-gray-50 transition-colors"
              >
                <p className="text-sm text-gray-600">Cliquez pour reprendre la conversation...</p>
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
